import { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Loader2, Check, Gift } from 'lucide-react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

export default function NewsletterForm({ settings }) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [couponCode, setCouponCode] = useState(null);

  const t = (key, fallback) => settings?.texts?.[key] || fallback;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Inserisci la tua email');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/newsletter/subscribe', { email });
      setSuccess(true);
      setCouponCode(response.data.coupon_code);
      toast.success(t('msg_newsletter_success', 'Iscrizione completata!'));
    } catch (error) {
      const message = error.response?.data?.detail || 'Errore nell\'iscrizione';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (success && couponCode) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-gradient-to-r from-primary/10 to-primary/5 p-8 text-center"
        data-testid="newsletter-success"
      >
        <div className="w-16 h-16 bg-primary text-primary-foreground rounded-full flex items-center justify-center mx-auto mb-4">
          <Gift size={28} />
        </div>
        <h3 className="font-heading text-2xl mb-2">Grazie per l'iscrizione!</h3>
        <p className="text-muted-foreground mb-4">Ecco il tuo codice sconto del 15%:</p>
        <div className="bg-background border-2 border-dashed border-primary px-6 py-3 inline-block">
          <span className="font-mono text-2xl font-bold tracking-wider">{couponCode}</span>
        </div>
        <p className="text-sm text-muted-foreground mt-4">
          Usalo al checkout. Valido 6 mesi, utilizzabile una sola volta.
        </p>
      </motion.div>
    );
  }

  return (
    <div className="bg-muted/50 p-8 lg:p-12" data-testid="newsletter-form">
      <div className="max-w-xl mx-auto text-center">
        <Mail size={32} className="mx-auto mb-4 text-primary" />
        <h3 className="font-heading text-2xl mb-2">
          {t('newsletter_title', 'Resta Aggiornato')}
        </h3>
        <p className="text-muted-foreground mb-6">
          {t('newsletter_subtitle', 'Iscriviti alla newsletter e ricevi il 15% di sconto sulla tua prima prenotazione')}
        </p>
        <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3">
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('newsletter_placeholder', 'La tua email')}
            className="flex-1"
            data-testid="newsletter-email-input"
          />
          <Button type="submit" disabled={loading} data-testid="newsletter-submit-btn">
            {loading ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              t('btn_subscribe', 'Iscriviti')
            )}
          </Button>
        </form>
        <p className="text-xs text-muted-foreground mt-4">
          Iscrivendoti accetti la nostra <a href="/privacy" className="underline">privacy policy</a>.
        </p>
      </div>
    </div>
  );
}
